"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Check, X, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
interface ComparisonRow {
  area: string;
  traditional: React.ReactNode;
  revbaba: React.ReactNode;
}
const comparisonRows: ComparisonRow[] = [{
  area: "Pricing Updates",
  traditional: "Static rates updated weekly or monthly without tracking pickup.",
  revbaba: <>
        Daily dynamic rate adjustments matching compset pace and demand compression for practical{" "}
        <Link href="https://hotelrevbaba.com/how-to-increase-hotel-adr-5-datadriven-strategies/" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors not-italic">
          ADR optimization
        </Link>
        .
      </>
}, {
  area: "OTA Parity Monitoring",
  traditional: "Spot-checked by front desk only when guests complain.",
  revbaba: "Continuous auditing to catch and eliminate unauthorized OTA discounting and cashbacks."
}, {
  area: "Length of Stay Control",
  traditional: "One-night bookings allowed during peak holiday compression.",
  revbaba: "Strict MLOS hurdles and minimum rate fences to protect weekend room yield."
}, {
  area: "Channel Balance",
  traditional: "High-commission OTAs take first pick of prime inventory.",
  revbaba: <>
        Inventory routed toward direct bookings and low-cost channels using proven{" "}
        <Link href="https://hotelrevbaba.com/channel-management-best-practices-for-2025/" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors not-italic">
          channel management best practices
        </Link>
        .
      </>
}, {
  area: "Accountability",
  traditional: "Busy GM juggles guest operations and complicated spreadsheets.",
  revbaba: "Sunil Singh reviews pickup numbers, extranet rules, and pricing daily."
}];
export default function TheHotelRevbabaAdvantage() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="2405" id="the-hotelrevbaba-advantage" className="relative overflow-hidden bg-background py-20 md:py-28">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px)",
      backgroundSize: "28px 28px"
    }} />

      <div ref={ref} className={["relative mx-auto max-w-7xl px-6 lg:px-8 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="font-mono text-xs font-semibold tracking-[0.2em] text-secondary uppercase">
              Side-by-side comparison
            </span>
          </div>

          <h2 className="font-default text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-foreground leading-[1.2]">
            In-House Guesswork vs. Managed Revenue Strategy
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Hotels lose 12% to 20% of top-line revenue to hidden OTA discounting, undetected undercutting, and static rates. Here is how daily active yield management compares to standard in-house operations.
          </p>
        </div>

        {/* Semantic Comparison Table with Horizontal Scroll for Mobile */}
        <div className="overflow-x-auto rounded-xl border border-border shadow-md bg-card">
          <table className="w-full min-w-[640px] text-left border-collapse">
            <caption className="sr-only">
              Comparison between traditional in-house hotel operations and HotelRevBaBa managed revenue strategy
            </caption>
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th scope="col" className="w-1/4 py-4 px-5 font-mono text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary-foreground border-b border-border/40">
                  Operational Area
                </th>
                <th scope="col" className="w-[37.5%] py-4 px-5 font-mono text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary-foreground/80 border-b border-border/40">
                  Traditional / In-House Guesswork
                </th>
                <th scope="col" className="w-[37.5%] py-4 px-5 font-mono text-xs sm:text-sm font-bold uppercase tracking-wider text-secondary border-b border-secondary/50 border-l-2 border-l-secondary/40 bg-primary/95">
                  HotelRevBaBa Managed Revenue Strategy
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {comparisonRows.map((row, idx) => {
              const isEven = idx % 2 === 0;
              return <tr key={row.area} className={isEven ? "bg-background/40" : "bg-muted/15"}>
                    {/* Operational Area (Row Header) */}
                    <th scope="row" className="py-5 px-5 font-semibold text-foreground align-top">
                      {row.area}
                    </th>

                    {/* Traditional / In-House */}
                    <td className="py-5 px-5 text-muted-foreground align-top">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground/80">
                          <X className="size-3" aria-hidden="true" />
                        </span>
                        <span className="leading-relaxed">{row.traditional}</span>
                      </div>
                    </td>

                    {/* HotelRevBaBa Managed Strategy */}
                    <td className="py-5 px-5 bg-secondary/[0.04] border-l-2 border-l-secondary/60 text-foreground align-top">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
                          <Check className="size-3.5" strokeWidth={2.5} aria-hidden="true" />
                        </span>
                        <span className="leading-relaxed font-medium text-foreground">
                          {row.revbaba}
                        </span>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Bottom CTA Row */}
        <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-xl bg-muted/30 border border-border/80 p-6 sm:p-8">
          <div>
            <h3 className="font-default text-lg sm:text-xl font-bold text-foreground">
              Ready to stop rate leaks across your hotel's distribution?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Sunil Singh will review your live extranets, compset pricing, and direct channel booking engine. Read how a{" "}
              <Link href="https://hotelrevbaba.com/how-to-conduct-a-hotel-revenue-audit-complete-guide/" className="underline text-secondary-foreground hover:text-foreground font-semibold transition-colors">
                free revenue audit
              </Link>{" "}
              works before you book.
            </p>
          </div>
          <Link to="/ContactPage#start-your-revenue-journey" className="inline-flex items-center justify-center gap-2 rounded-lg bg-secondary px-6 py-3 text-sm font-semibold text-primary shadow-sm hover:opacity-90 transition-opacity whitespace-nowrap">
            <span>Request a Free Revenue Audit</span>
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>;
}